import { useState, useEffect } from 'react';
import { evaluate } from '@mdx-js/mdx';
import * as runtime from 'react/jsx-runtime';
import rehypeKatex from 'rehype-katex';
import remarkFrontmatter from 'remark-frontmatter';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import type { EvaluateOptions } from '@mdx-js/mdx';

export type MdxContentComponent = (props: { components?: Record<string, unknown> }) => React.JSX.Element;

interface MdxComponentState {
  content: string;
  Component: MdxContentComponent | null;
  isEvaluating: boolean;
  error: string | null;
}

export function useMdxComponent(content: string): MdxComponentState {
  const [state, setState] = useState<MdxComponentState>({
    content,
    Component: null,
    isEvaluating: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    evaluate(content, {
      ...(runtime as unknown as EvaluateOptions),
      remarkPlugins: [remarkFrontmatter, remarkGfm, remarkMath],
      rehypePlugins: [rehypeKatex],
    })
      .then(({ default: Comp }) => {
        if (!cancelled) {
          setState({
            content,
            Component: Comp as MdxContentComponent,
            isEvaluating: false,
            error: null,
          });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({ content, Component: null, isEvaluating: false, error: String(err) });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [content]);

  if (state.content !== content) {
    return { content, Component: null, isEvaluating: true, error: null };
  }

  return state;
}
